import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'
import { fetchApiDataIfNeeded, callApi, callFileApi } from '../../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import SHSForm from '../../utils/shs-form'
import InputElement from '../../utils/input'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SearchComplete from '../../utils/search-complete'
import { createSelector } from '@reduxjs/toolkit'

const selectAdminGroupQuestions = (state, ownProps) => state.adminGroupQuestions.filter(gq => gq.questionId === Number(ownProps.params.questionId))
const selectAdminInputGroups = (state) => state.adminInputGroups
const selectAdminQuestion = (state, ownProps) => state.adminQuestions.find(q => q.id === Number(ownProps.params.questionId))
const selectQuestionExamples = state => state.questionExamples
const selectImages = state => state.images

const makeAdminInputGroups = createSelector(
    [selectAdminInputGroups, selectAdminGroupQuestions],
    (adminInputGroups, adminGroupQuestions) => {
        const groupIds = adminGroupQuestions.map(gq => gq.groupId)
        return adminInputGroups.filter(ig => groupIds.includes(ig.id))
    }
)

const makeQuestionExample = createSelector(
    [selectAdminQuestion, selectQuestionExamples],
    (adminQuestion, questionExamples) => {
        if (!adminQuestion) {
            return null
        }
        return questionExamples.find(ex => ex.questionId === adminQuestion.id)
    })

const makeQuestionExampleImage = createSelector(
    [makeQuestionExample, selectImages],
    (questionExample, images) => {
        if (!questionExample) {
            return null
        }
        return images.find(img => img.id === questionExample.imageId)
    })

const mapStateToProps = (state, ownProps) => {
    const adminQuestion = selectAdminQuestion(state, ownProps)
    const questionExample = makeQuestionExample(state, ownProps)
    const image = makeQuestionExampleImage(state, ownProps)
    return {
        adminQuestion,
        questionExample,
        image,
        configs: state.configs.filter(config => config.field === 'QUESTION_TYPES'),
        adminGroupQuestions: selectAdminGroupQuestions(state, ownProps),
        adminInputGroups: makeAdminInputGroups(state, ownProps)
    }
}

const AdminQuestionEdit = ({ dispatch, navigate, params, adminQuestion, questionExample, image, adminGroupQuestions, adminInputGroups, configs }) => {
    const [typeId, setTypeId] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview)
            }
        }
    }, [imagePreview])

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/questions', 'VIEW', params.questionId)))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('configs', 'LIST', null), { filter: ['field|QUESTION_TYPES'] }))
    }, [dispatch, params.questionId])

    useEffect(() => {
        if (adminQuestion && !typeId) {
            setTypeId(adminQuestion._computed.typeValue)
        }
    }, [adminQuestion, typeId])

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/group-questions', 'LIST'),
            { 'filter': [`question_id|${params.questionId}`], 'no-pagination': true }))
    }, [dispatch, params.questionId])

    // If this is an array, it causes an infinite loop in useEffect.  Joined to make a string.  Is there a better way?
    const groupIds = adminGroupQuestions.map(gq => gq.groupId).join(',')

    useEffect(() => {
        if (groupIds) {
            const filter = groupIds.split(',').map(id => `id|${id}`)
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-groups', 'LIST'),
                { 'filter': filter, 'filterMethod': 'OR', 'no-pagination': true }))
        }
    }, [dispatch, groupIds])

    const typeOptions = () => {
        if (!configs || !adminQuestion) {
            return null
        }
        return configs.map(config => {
            return ({ value: config.id, label: config.display })
        })
    }

    const handleAddQuestion = async (group) => {
        dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'UPDATE', group.id, 'add-question'), { questionId: adminQuestion.id }, -1))
            .then(() => {
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-groups', 'VIEW', group.id)))
            })
    }

    const renderGroupSearch = () => {
        const excludeGroupIds = adminGroupQuestions.map(gq => gq.groupId) || []
        let excludeFilter = []
        if (excludeGroupIds.length > 0) {
            excludeFilter = [`id|${excludeGroupIds.join(',')}|WHERE_NOT_IN`]
        }
        const currentGroups = adminInputGroups.map(g => <li key={g.id}>{g.name}</li>)
        // const currentGroupsNames = adminInputGroups.map(g => g.name).join(', ')
        return (
            <>
                <p>Current Groups: { currentGroups && <ul>{currentGroups}</ul>}</p>
                <div className="search-complete">
                    <SearchComplete
                        key={adminInputGroups.length}
                        label="Search Groups to Add Questions"
                        searchEndpoint="admin/input-groups"
                        renderTemplate={(group) => group.name}
                        excludeFilter={excludeFilter}
                        onHandleSelect={handleAddQuestion}
                        allowCreation={false}
                        placeholder="Search Groups to Add Questions" />
                </div>
            </>
        )
    }


    const submitFX = (formData, formId) => {
        if (typeId === 'EXAMPLE_PHOTO') {
            dispatch(callFileApi(apiResourceEndpoint('admin/questions', 'UPDATE', adminQuestion.id), formData, formId))
        } else {
            dispatch(callApi(apiResourceEndpoint('admin/questions', 'UPDATE', adminQuestion.id), formData, formId))
        }
    }

    const successFX = () => {
        navigate(buildRoutePath(routes.adminQuestionIndex.path))
    }

    const handleImagePreview = (id, file) => {
        if (file) {
            setImagePreview(URL.createObjectURL(file))
        }
    }

    const handleDelete = () => {
        const confirm = window.confirm('Are you sure you want to delete this question?')
        if (confirm) {
            dispatch(callApi(apiResourceEndpoint('admin/questions', 'DELETE', adminQuestion.id), {}, -1))
                .then(() => {
                    navigate(buildRoutePath(routes.adminQuestionIndex.path))
                })
        }
    }

    if (!adminQuestion) {
        return <div>Loading...</div>
    }

    return (
        <div className="admin-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        <h1>Edit Question</h1>
                        <p>Editing a question will affect any templates that use this question, so be sure to check the templates that use this question after making edits.</p>
                    </div>
                </div>
            </div>
            <div className="content-wrapper">
                <div className="container">
                    <div className="content-box">
                        <SHSForm extraClass="flex-form" submitFX={submitFX} successFX={successFX} useFormData={typeId === 'EXAMPLE_PHOTO'} showCancel={true}
                            cancelAction={() => navigate(buildRoutePath(routes.adminQuestionIndex.path))}>
                            <InputElement
                                type="text"
                                id="label"
                                label="Question Title"
                                default={adminQuestion.label}
                                extraClass="flex-50"
                                required />
                            <InputElement
                                type="text"
                                id="instructions"
                                label="Instructions"
                                default={adminQuestion.instructions}
                                extraClass="flex-50" />
                            <InputElement
                                type="valueSelect"
                                id="typeId"
                                label="Type"
                                default={adminQuestion.typeId}
                                onChange={(id, value) => {
                                    const selectedOption = configs.find(c => c.id === parseInt(value, 10))
                                    setTypeId(selectedOption.value)
                                }}
                                options={typeOptions()}
                                extraClass="flex-50"
                                required />
                            {typeId === 'EXAMPLE_PHOTO' &&
                            <>
                                <InputElement
                                    accept="image/*"
                                    type="file"
                                    id="exampleImage"
                                    onChange={handleImagePreview} />
                                {(imagePreview || image) &&
                                <div className="example-photo image-wrapper" style={{ marginTop: '1rem' }} >
                                    <img src={imagePreview || process.env.REACT_APP_API_URL + apiResourceEndpoint(`questions`, 'VIEW', adminQuestion.id, `example-photo/${image.id}`).url + '?style=thumbnail'} alt="answer value" />
                                </div>
                                }
                                <InputElement
                                    type="text"
                                    id="exampleCaption"
                                    label="Describe the example photo"
                                    placeholder="Details about why this is a good example"
                                    default={questionExample?.caption} />
                            </>
                            }
                            {typeId === 'IMEI' ?
                                <p className="warning">Note: The options for this question will be available to the installer as a list of IMEIs associated with the account.</p>
                                :
                                <InputElement
                                    type="text"
                                    id="options"
                                    default={adminQuestion.options}
                                    placeholder="Prompts or options for the user"
                                    extraClass="flex-50" />
                            }
                            {typeId === 'TEXT' &&
                                <InputElement
                                    type="text"
                                    id="defaultValue"
                                    label="Default Value (not required)"
                                    placeholder="A default answer to the question"
                                    extraClass="flex-50"
                                    default={adminQuestion.defaultValue} />
                            }
                        </SHSForm>
                    </div>
                </div>
                <div className="container">
                    <div className="content-box">
                        <h3>Add question to an existing group</h3>
                        {renderGroupSearch()}
                    </div>
                </div>
                <div className="container">
                    <div className="content-box delete-wrapper">
                        <div className="btn small red" onClick={handleDelete}>
                            DELETE THIS QUESTION <FontAwesomeIcon icon="trash-alt" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(AdminQuestionEdit))
